import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, AlertTriangle, X, Skull } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const PROVIDER_LABELS = {
  nuvemshop: "Nuvemshop",
  bling: "Bling",
  mercadolivre: "Mercado Livre",
};

/**
 * Big-red-button destructive action.
 *
 * Flow:
 *   1. Fetch preview counts per provider
 *   2. User selects which providers to hit (all by default)
 *   3. User must type the exact phrase "APAGAR TODOS"
 *   4. User must supply a reason ≥ 10 chars (audit trail)
 *   5. Backend does the work; we show per-provider results
 */
export default function BulkDeleteAllModal({ open, onClose, onCompleted }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [phrase, setPhrase] = useState("");
  const [reason, setReason] = useState("");
  const [providers, setProviders] = useState({ nuvemshop: true, bling: true, mercadolivre: true });
  const [executing, setExecuting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!open) {
      setPhrase("");
      setReason("");
      setResult(null);
      setPreview(null);
      return;
    }
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/erp/bulk-delete-all/preview");
        setPreview(data);
      } catch (e) {
        toast.error(formatApiErrorDetail(e) || "Erro ao carregar preview");
      } finally {
        setLoading(false);
      }
    })();
  }, [open]);

  if (!open) return null;

  const selectedProviders = Object.entries(providers).filter(([, v]) => v).map(([k]) => k);
  const total = preview?.total || 0;
  const totalSelected = selectedProviders.reduce(
    (sum, p) => sum + (preview?.by_provider?.[p] || 0), 0,
  );
  const phraseOK = phrase.trim() === "APAGAR TODOS";
  const reasonOK = reason.trim().length >= 10;
  const canConfirm = phraseOK && reasonOK && totalSelected > 0 && !executing;

  const execute = async () => {
    if (!canConfirm) return;
    setExecuting(true);
    setResult(null);
    try {
      const { data } = await api.post("/erp/bulk-delete-all", {
        reason: reason.trim(),
        confirm_phrase: phrase.trim(),
        providers: selectedProviders,
      });
      setResult(data);
      if (data.deleted > 0) {
        toast.success(`${data.deleted} produto(s) apagado(s)`);
      }
      if (data.failed > 0) {
        toast.warning(`${data.failed} falha(s) — veja os detalhes`);
      }
      if (onCompleted) onCompleted(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Erro ao apagar");
    } finally {
      setExecuting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="bulk-delete-all-modal">
      <div className="bg-white rounded-lg max-w-lg w-full shadow-xl">
        {/* Header — red band for danger */}
        <div className="bg-red-600 text-white px-6 py-4 rounded-t-lg flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Skull className="w-5 h-5" />
            <h2 className="font-bold text-lg">Apagar TODOS os produtos publicados</h2>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white" data-testid="bulk-delete-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {!result ? (
            <>
              <div className="bg-amber-50 border border-amber-300 rounded-md p-3 text-sm text-amber-900 flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <div>
                  <strong>Ação IRREVERSÍVEL.</strong> Vamos apagar todos os produtos que você publicou pelo Magic SIP em cada plataforma marcada abaixo.
                  Nuvemshop e Bling deletam permanentemente. Mercado Livre fecha o anúncio (não pode ser reaberto).
                  Isso NÃO cancela pedidos existentes nem afeta produtos que você cadastrou fora do Magic SIP.
                </div>
              </div>

              {/* Preview */}
              <div>
                <Label className="text-xs uppercase text-zinc-500">Impacto</Label>
                {loading ? (
                  <div className="text-sm text-zinc-500 mt-2 flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" /> Carregando contagens...
                  </div>
                ) : preview ? (
                  <div className="mt-2 space-y-2" data-testid="bulk-delete-preview">
                    {["nuvemshop", "bling", "mercadolivre"].map((p) => {
                      const count = preview.by_provider?.[p] || 0;
                      return (
                        <label
                          key={p}
                          className={`flex items-center justify-between rounded-md border px-3 py-2 text-sm cursor-pointer transition-colors ${
                            providers[p] ? "border-red-300 bg-red-50" : "border-zinc-200 bg-white"
                          } ${count === 0 ? "opacity-50" : ""}`}
                        >
                          <div className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={!!providers[p]}
                              disabled={count === 0}
                              onChange={(e) => setProviders((s) => ({ ...s, [p]: e.target.checked }))}
                              className="accent-red-600"
                              data-testid={`bulk-delete-provider-${p}`}
                            />
                            <span className="font-medium">{PROVIDER_LABELS[p]}</span>
                          </div>
                          <span className={`font-mono ${count > 0 ? "text-red-700 font-bold" : "text-zinc-400"}`}>
                            {count} produto{count === 1 ? "" : "s"}
                          </span>
                        </label>
                      );
                    })}
                    <div className="text-xs font-mono text-zinc-500 pt-1">
                      Total a apagar: <strong className="text-red-700">{totalSelected}</strong> de {total}
                    </div>
                  </div>
                ) : null}
              </div>

              {/* Reason */}
              <div>
                <Label htmlFor="bulk-delete-reason" className="text-xs uppercase text-zinc-500">
                  Motivo (auditoria — ≥ 10 caracteres)
                </Label>
                <textarea
                  id="bulk-delete-reason"
                  data-testid="bulk-delete-reason"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Ex: teste em produção, refazer catálogo com novas fotos..."
                  className="mt-1.5 w-full min-h-[70px] rounded-md border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-600"
                />
                {!reasonOK && reason.length > 0 && (
                  <div className="text-xs text-red-600 mt-1">Motivo muito curto ({reason.trim().length}/10)</div>
                )}
              </div>

              {/* Confirmation phrase */}
              <div>
                <Label htmlFor="bulk-delete-phrase" className="text-xs uppercase text-zinc-500">
                  Digite exatamente: <span className="font-mono text-red-700">APAGAR TODOS</span>
                </Label>
                <Input
                  id="bulk-delete-phrase"
                  data-testid="bulk-delete-phrase"
                  value={phrase}
                  onChange={(e) => setPhrase(e.target.value)}
                  autoComplete="off"
                  spellCheck={false}
                  placeholder="APAGAR TODOS"
                  className="mt-1.5 h-10 rounded-md border-zinc-300 font-mono uppercase focus-visible:ring-2 focus-visible:ring-red-600"
                />
              </div>

              <div className="flex gap-2 pt-2">
                <Button
                  variant="outline"
                  onClick={onClose}
                  className="flex-1 rounded-md border-zinc-300 h-10"
                  data-testid="bulk-delete-cancel"
                  disabled={executing}
                >
                  Cancelar
                </Button>
                <Button
                  onClick={execute}
                  disabled={!canConfirm}
                  className="flex-1 rounded-md h-10 bg-red-600 hover:bg-red-700 text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="bulk-delete-confirm"
                >
                  {executing ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Apagando...</>
                  ) : (
                    `Apagar ${totalSelected} produto${totalSelected === 1 ? "" : "s"}`
                  )}
                </Button>
              </div>
            </>
          ) : (
            /* Result view */
            <div className="space-y-4" data-testid="bulk-delete-result">
              <div className="text-center py-2">
                <div className="text-4xl font-bold text-emerald-600">{result.deleted}</div>
                <div className="text-sm text-zinc-500">produtos apagados</div>
                {result.failed > 0 && (
                  <div className="text-xs text-amber-700 mt-1">{result.failed} falha(s)</div>
                )}
              </div>
              <div className="space-y-2">
                {Object.entries(result.by_provider || {}).map(([prov, r]) => (
                  <div key={prov} className="border border-zinc-200 rounded-md p-3 text-sm">
                    <div className="flex justify-between">
                      <span className="font-medium">{PROVIDER_LABELS[prov]}</span>
                      <span className="font-mono">
                        <span className="text-emerald-700">✓ {r.deleted || 0}</span>
                        {" · "}
                        <span className={r.failed ? "text-red-700" : "text-zinc-400"}>
                          ✗ {r.failed || 0}
                        </span>
                      </span>
                    </div>
                    {r.message && (
                      <div className="text-xs text-zinc-500 mt-1">{r.message}</div>
                    )}
                  </div>
                ))}
              </div>
              <Button
                onClick={onClose}
                className="w-full rounded-md h-10 brand-primary text-white"
                data-testid="bulk-delete-done"
              >
                Fechar
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
